/* ============================================================================
   accessories.js — <accessories-component>, das Zubehoer der Produktseite und
   des Konfigurators
   ----------------------------------------------------------------------------
   Stand bis M6 als {% javascript %} in snippets/product-app-accessories.liquid
   und kam ueber compiled_assets/snippet-scripts.js auf jede Seite des Themes.
   Jetzt laedt das Snippet dieses Modul selbst, nur wo Zubehoer gerendert wird
   (R5).

   VERTRAG
     needsSelection()        ruft assets/product.js vor dem Kauf: angehakt,
                             aber im Popup nicht gespeichert -> Popup auf
     getSerializedItems()    ruft assets/configurator.js
     theme.addToCart(form)   Popup-Kaufknopf auf der Produktseite
                             (assets/product.js); im Konfigurator
                             window.CustomizableATC()
     theme.popupApp.clear()  assets/popup.js, nach dem Speichern
     nko:variant:change      (document) Verfuegbarkeit der Zubehoerkarten
     .minicart__accessories  Zeile „+ Etui …“ in der Kaufleiste
     `disabled` am Element  sperrt Klicks, bis dieses Modul laeuft

   Hier stand `initAccessories`, eine Kaufwache am <form>. Sie suchte
   `.product__combo--accessories` IM Formular — das Snippet steht aber neben
   <nko-product-form>, nie darin: die Wache bog immer sofort ab. Die Pflicht
   zur Auswahl setzt assets/product.js ueber `needsSelection()` durch.

   AUF- UND ABBAU: kein Aufbau im Konstruktor; alle Hoerer haengen an EINEM
   AbortController (`this.hoerer`), `disconnectedCallback` bricht ihn ab.
   ==========================================================================  */

class Accessories extends HTMLElement {
  /* Das Popup steht IN diesem Element (snippets/product-app-accessories.liquid);
     assets/popup.js verschiebt Popups nicht. Vorher suchte der Konstruktor es
     dokumentweit — dasselbe Element, nur ohne Bezug zu dieser Instanz. */
  get popup() {
    return this.querySelector('.popup--accessories');
  }

  /* Frisch abfragen statt merken: die Sticky-Leiste wird bei jedem
     Variantenwechsel ersetzt. Die Zeile selbst ueberlebt inzwischen den
     Tausch (PRESERVE_STATE in assets/product.js), eine gemerkte Referenz
     waere trotzdem die fragilere Wahl. */
  get miniAtcAccessoriesEl() {
    return document.querySelector('.minicart__accessories');
  }

  connectedCallback() {
    this.hoerer?.abort();
    this.hoerer = new AbortController();
    const { signal } = this.hoerer;

    this.checkboxes = this.querySelectorAll('[data-accessory-checkbox]');
    this.selects = this.querySelectorAll('[data-accessory-select]');

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', this.handleCheckboxChange.bind(this), { signal });
    });

    this.selects.forEach((select) => {
      select.addEventListener('change', this.handleSelectChange.bind(this), { signal });
    });

    const saveBtn = this.popup.querySelector('.js-accessories-save');
    saveBtn.addEventListener('click', this.handleSave.bind(this), { signal });

    const atcBtn = this.popup.querySelector('.js-accsessories-submit');
    atcBtn.addEventListener('click', this.handleAddToCart.bind(this), { signal });

    /* War ein jQuery-Hoerer auf `variantChange`. assets/product.js feuert
       stattdessen `nko:variant:change` (bubbles, Nutzlast in `detail`) —
       dasselbe zum Zeitpunkt, nur ohne jQuery. `e.detail` hat die Form
       `{ variant, sectionId, productId }`, deshalb kann
       `updateGeneralAvailability` unveraendert bleiben: es liest
       `e.variant?.available`. */
    const updateGeneralAvailability = this.updateGeneralAvailability;
    document.addEventListener('nko:variant:change', (e) => updateGeneralAvailability(e.detail), { signal });

    this.classList.remove('disabled');
  }

  disconnectedCallback() {
    this.hoerer?.abort();
    this.hoerer = null;
  }

  handleCheckboxChange(e) {
    const checkbox = e.target;
    const popupLi = this.querySelector(`[data-accessory-popup-item][data-entry-id="${checkbox.dataset.entryId}"]`);

    // Show/hide the entire popup list item
    if (checkbox.checked) {
      popupLi.classList.add('is-visible');
      popupLi.classList.remove('is-hidden');
    } else {
      popupLi.classList.remove('is-visible');
      popupLi.classList.add('is-hidden');
    }

    // Enable/disable the product form inputs
    const inputs = popupLi.querySelectorAll('[data-item-input]');
    inputs.forEach((input) => {
      input.disabled = !checkbox.checked;
    });

    // Update popup size
    const checkedCount = this.querySelectorAll('[data-accessory-checkbox]:checked').length;
    if (checkedCount > 1) {
      this.popup.classList.add('popup--xl');
      this.popup.classList.remove('popup--lg');
    } else {
      this.popup.classList.remove('popup--xl');
      this.popup.classList.add('popup--lg');
    }

    // Update sticky ATC
    if (this.miniAtcAccessoriesEl) {
      this.updateMiniAtc();
    }
  }

  handleSelectChange(e) {
    const selectedOption = e.target.options[e.target.selectedIndex];
    const variantId = selectedOption.value;
    const bundleItem = e.target.closest('[data-accessory-popup-item]');
    const imageContainers = bundleItem.querySelectorAll('.bundle__image');

    // Update image next to select
    imageContainers.forEach((el) => {
      if (el.dataset.variantId === variantId) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });

    // Update lens cat
    const lensCatEl = bundleItem.querySelector('lens-category-widget');
    if (lensCatEl) {
      lensCatEl.setAttribute('data-variant-title', selectedOption.dataset.variantTitle);
    }
  }

  handleAddToCart() {
    this.handleSave();
    if (window.CustomizableATC) {
      window.CustomizableATC();
    } else {
      theme.addToCart(document.querySelector('.js-product-form'));
    }
  }

  handleSave() {
    const bundlePopupItems = this.querySelectorAll('[data-accessory-popup-item]');
    bundlePopupItems.forEach((popupLi) => {
      const accessorySelect = popupLi.querySelector(
        `[data-accessory-select][data-entry-id="${popupLi.dataset.entryId}"]`
      );

      accessorySelect.dataset.saved = 'true';

      const selectedOption = accessorySelect.querySelector('option:checked');
      const price = selectedOption.dataset.price;
      const compareAtPrice = selectedOption.dataset.compareAtPrice;
      const variantId = selectedOption.value;

      const previewItem = this.querySelector(`.group--accessories[data-accessory-id="${popupLi.dataset.entryId}"]`);

      // Update prices
      previewItem.querySelector('.price--default').innerHTML = price;
      if (compareAtPrice) {
        previewItem.querySelector('.price--old').innerHTML = compareAtPrice;
      }
      previewItem.querySelector('.price__from')?.remove();

      // Update image
      previewItem.querySelectorAll('.group__image img').forEach((imageEl) => {
        if (imageEl.dataset.variantId === variantId) {
          imageEl.removeAttribute('hidden');
        } else {
          imageEl.setAttribute('hidden', '');
        }
      });

      // Update sticky ATC
      if (this.miniAtcAccessoriesEl) {
        this.updateMiniAtc();
      }
    });

    // Close the popup
    theme.popupApp.clear();
  }

  updateGeneralAvailability(e) {
    const isUnavailable = e.variant?.available === false;

    const availabilityElements = document.querySelectorAll('accessories-component [data-availability-status]');

    if (!isUnavailable) {
      availabilityElements.forEach((el) => {
        el.dataset.availabilityStatus = 'available';
      });
    } else {
      availabilityElements.forEach((el) => {
        el.dataset.availabilityStatus = 'unavailable';
      });
    }
  }

  updateMiniAtc() {
    const zeile = this.miniAtcAccessoriesEl;
    if (!zeile) return;
    zeile.innerHTML = '';
    const checkedPreviewItems = [...this.checkboxes]
      .filter((cb) => cb.checked)
      .map((c) => c.closest('.group--accessories'));
    checkedPreviewItems.forEach((previewItem) => {
      const accTitle = previewItem.querySelector('.group__title').innerHTML;
      const accPrice = previewItem.querySelector('.price--default').innerHTML;
      zeile.innerHTML += ` + ${accTitle} (${accPrice})<br />`;
    });
  }

  needsSelection() {
    let needsSelection = false;
    this.checkboxes.forEach((cb) => {
      const accessorySelect = this.querySelector(`[data-accessory-select][data-entry-id="${cb.dataset.entryId}"]`);
      const isSaved = accessorySelect.dataset.saved === 'true';
      if (cb.checked && !isSaved) {
        needsSelection = true;
      }
    });
    return needsSelection;
  }

  getSerializedItems() {
    const selectedItems = [...this.checkboxes]
      .filter((cb) => cb.checked)
      .map((cb) => {
        const entryId = cb.dataset.entryId;
        const accessorySelect = this.querySelector(`[data-accessory-select][data-entry-id="${entryId}"]`);
        const selectedOption = accessorySelect.querySelector('option:checked');
        const accessoryIdInputValue = this.querySelector(
          `[name="items[Accessory-${entryId}]properties[_accessoryId]"]`
        ).value;
        const baseProductIdInputValue = this.querySelector(
          `[name="items[Accessory-${entryId}]properties[_baseProductId]"]`
        ).value;
        return {
          id: selectedOption.value,
          quantity: 1,
          properties: {
            _accessoryId: accessoryIdInputValue,
            _baseProductId: baseProductIdInputValue,
          },
        };
      });
    return selectedItems;
  }
}

if (!customElements.get('accessories-component')) {
  customElements.define('accessories-component', Accessories);
}

/* ----------------------------------------------------------------------------
   NUR ALS MODUL LADEN: <script src="…" type="module">, nie mit `defer`.
   Module laufen wie `defer` nach dem Parsen in Dokumentreihenfolge, aber nur
   einmal je URL und im strict mode.
   ---------------------------------------------------------------------------- */
